import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CookiePolicyPage = () => {
  const sectionHeadingClass = 'text-xl md:text-2xl font-bold text-[#1F3A68] mt-8 mb-3';
  const paragraphClass = 'text-slate-700 leading-relaxed mb-3';
  const listClass = 'list-disc pl-6 space-y-1 text-slate-700 mb-3';

  const openPreferences = () => {
    // CookieConsentBanner listens for this event
    window.dispatchEvent(new CustomEvent('open-cookie-preferences'));
  };

  return (
    <div className="min-h-screen bg-[#F4F2E9]">
      <Header />
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-10">
          <h1 className="text-3xl md:text-4xl font-bold text-[#1F3A68] mb-2">Cookie Policy</h1>
          <p className="text-sm text-slate-500 mb-8">Last updated: 19 April 2026</p>

          <section className="mb-8">
            <p className={paragraphClass}>
              This Cookie Policy explains what cookies and similar technologies we use on the Ariv platform, why we use them, and how you can control them. It sits alongside our <Link to="/privacy" className="text-[#1F3A68] underline">Privacy Policy</Link> and <Link to="/terms" className="text-[#1F3A68] underline">Terms of Service</Link>.
            </p>
            <p className={paragraphClass}>
              You can change your consent at any time using the button below.
            </p>
            <button
              onClick={openPreferences}
              className="inline-flex items-center gap-2 bg-[#1F3A68] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#1F3A68]/90 transition-colors"
            >
              Manage cookie preferences
            </button>
          </section>

          <h2 className={sectionHeadingClass}>1. What is a cookie?</h2>
          <p className={paragraphClass}>
            A cookie is a small text file that a website places on your device. It helps the site remember your actions and preferences (such as login state) and, in some cases, measure how the site is used. We also use similar technologies such as local storage and session storage, and we refer to all of these collectively as "cookies" in this policy.
          </p>

          <h2 className={sectionHeadingClass}>2. How we categorise cookies</h2>
          <ul className={listClass}>
            <li><strong>Strictly necessary:</strong> required for the Platform to work. These do not need consent under the Privacy and Electronic Communications Regulations (PECR).</li>
            <li><strong>Functional:</strong> remember preferences such as your language or your cookie choices. Consent required.</li>
            <li><strong>Analytics:</strong> help us understand how the Platform is used so we can improve it. Consent required.</li>
            <li><strong>Marketing / advertising:</strong> not currently used on Ariv.</li>
          </ul>

          <h2 className={sectionHeadingClass}>3. Cookies we use</h2>
          <div className="overflow-x-auto mb-4">
            <table className="min-w-full text-sm border border-slate-200 rounded-lg">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3 border-b border-slate-200">Name</th>
                  <th className="text-left p-3 border-b border-slate-200">Purpose</th>
                  <th className="text-left p-3 border-b border-slate-200">Category</th>
                  <th className="text-left p-3 border-b border-slate-200">Duration</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                <tr>
                  <td className="p-3 border-b border-slate-100 font-mono">authToken</td>
                  <td className="p-3 border-b border-slate-100">Keeps you signed in. Stored in local storage.</td>
                  <td className="p-3 border-b border-slate-100">Strictly necessary</td>
                  <td className="p-3 border-b border-slate-100">Until logout or 7 days</td>
                </tr>
                <tr>
                  <td className="p-3 border-b border-slate-100 font-mono">refresh_token</td>
                  <td className="p-3 border-b border-slate-100">HTTP-only cookie used to renew your session securely.</td>
                  <td className="p-3 border-b border-slate-100">Strictly necessary</td>
                  <td className="p-3 border-b border-slate-100">7 days</td>
                </tr>
                <tr>
                  <td className="p-3 border-b border-slate-100 font-mono">user</td>
                  <td className="p-3 border-b border-slate-100">Caches your profile to speed up page loads.</td>
                  <td className="p-3 border-b border-slate-100">Strictly necessary</td>
                  <td className="p-3 border-b border-slate-100">Until logout</td>
                </tr>
                <tr>
                  <td className="p-3 border-b border-slate-100 font-mono">ariv_cookie_consent</td>
                  <td className="p-3 border-b border-slate-100">Remembers your cookie choice so we don't ask again.</td>
                  <td className="p-3 border-b border-slate-100">Functional</td>
                  <td className="p-3 border-b border-slate-100">12 months</td>
                </tr>
                <tr>
                  <td className="p-3 border-b border-slate-100 font-mono">ph_* (PostHog)</td>
                  <td className="p-3 border-b border-slate-100">Anonymised product analytics — which pages are visited and which features are used. Loaded only after you accept analytics cookies.</td>
                  <td className="p-3 border-b border-slate-100">Analytics</td>
                  <td className="p-3 border-b border-slate-100">12 months</td>
                </tr>
                <tr>
                  <td className="p-3 border-b border-slate-100 font-mono">ariv_acquisition</td>
                  <td className="p-3 border-b border-slate-100">Remembers which marketing link brought you to Ariv (its utm_ tags, such as the partner&apos;s name), so we can count sign-ups from each marketing partner. Stored in local storage only after you accept analytics cookies, and deleted if you withdraw that consent. Without consent the tags are kept in memory for your current visit only.</td>
                  <td className="p-3 border-b border-slate-100">Analytics</td>
                  <td className="p-3 border-b border-slate-100">30 days</td>
                </tr>
                <tr>
                  <td className="p-3 border-b border-slate-100 font-mono">__stripe_mid, __stripe_sid</td>
                  <td className="p-3 border-b border-slate-100">Fraud-prevention cookies set by Stripe on payment pages.</td>
                  <td className="p-3 border-b border-slate-100">Strictly necessary</td>
                  <td className="p-3 border-b border-slate-100">30 min – 12 months</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className={sectionHeadingClass}>4. Managing your choices</h2>
          <p className={paragraphClass}>
            When you first visit Ariv we show a cookie banner with three options:
          </p>
          <ul className={listClass}>
            <li><strong>Accept all:</strong> enables analytics cookies as well as strictly-necessary cookies.</li>
            <li><strong>Reject non-essential:</strong> we load only the cookies needed for the Platform to work.</li>
            <li><strong>Manage preferences:</strong> choose category by category.</li>
          </ul>
          <p className={paragraphClass}>
            Strictly-necessary cookies cannot be refused because the Platform would not function without them — for example, you could not stay signed in.
          </p>
          <p className={paragraphClass}>
            You can also disable or delete cookies from your browser settings. Most browsers provide a step-by-step guide — see the "Help" menu.
          </p>

          <h2 className={sectionHeadingClass}>5. Third-party cookies</h2>
          <p className={paragraphClass}>
            Where a third party sets cookies through the Platform (for example, Stripe on payment pages) their own privacy and cookie notices apply:
          </p>
          <ul className={listClass}>
            <li>Stripe: <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">stripe.com/privacy</a></li>
            <li>PostHog: <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#1F3A68] underline">posthog.com/privacy</a></li>
          </ul>

          <h2 className={sectionHeadingClass}>6. Changes to this policy</h2>
          <p className={paragraphClass}>
            We may update this Cookie Policy to reflect changes in the tools we use. The "Last updated" date at the top of this page always reflects the current version.
          </p>

          <h2 className={sectionHeadingClass}>7. Contact</h2>
          <p className={paragraphClass}>
            Questions about cookies: <strong>privacy@brickandcapital.co.uk</strong>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CookiePolicyPage;
